
import { Link } from "react-router-dom";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useAppStore } from "@/lib/store";
import { getCategory } from "@/lib/data/categories";
import { formatPrice } from "@/lib/utils";
import RevealOnScroll from "@/components/ui/RevealOnScroll";
import ImmersiveBackground from "@/components/ui/ImmersiveBackground";
import { useDocumentMeta } from "@/lib/useDocumentMeta";

export default function CartPage() {
  useDocumentMeta({
    title: "Your Cart",
    description: "Review your cart on Nerd Multiverse.",
    path: "/cart",
    noindex: true,
  });

  const cart = useAppStore((s) => s.cart);
  const products = useAppStore((s) => s.products);
  const removeFromCart = useAppStore((s) => s.removeFromCart);
  const checkout = useAppStore((s) => s.checkout);
  const authReady = useAppStore((s) => s._authReady);
  const user = useAppStore((s) => s.currentUser());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const items = cart
    .map((c) => ({ ...c, product: products.find((p) => p.id === c.productId) }))
    .filter((c) => c.product);
  const total = items.reduce((sum, i) => sum + (i.product?.price ?? 0) * i.quantity, 0);

  const handleCheckout = async () => {
    if (!user) return;
    setError("");
    setSubmitting(true);
    try {
      // On success this redirects the browser to Stripe, so this component
      // unmounts — no need to clear `submitting` in that path.
      const started = await checkout();
      if (!started) setSubmitting(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't start checkout.");
      setSubmitting(false);
    }
  };

  return (
    <div className="relative pt-28 md:pt-36 pb-24 min-h-dvh">
      <ImmersiveBackground
        src="/images/hero/cart-v2.webp"
        mobileSrc="/images/hero/cart-mobile-v2.webp"
        focus="50% 25%"
      />
      <div className="nm-container max-w-3xl">
        <RevealOnScroll>
          <h1 className="font-display text-4xl font-semibold text-white">Cart</h1>
        </RevealOnScroll>

        {items.length === 0 ? (
          <div className="mt-16 text-center">
            <p className="font-display text-2xl text-white">Your cart is empty.</p>
            <p className="mt-2 text-[var(--nm-ink-muted)]">
              Your next find could be waiting somewhere in the Multiverse.
            </p>
            <Link
              to="/marketplace"
              className="mt-8 inline-block px-7 py-3.5 rounded-full bg-white text-[#05070d] text-sm font-medium tracking-wide hover:bg-[var(--nm-blue-bright)] hover:text-white transition-colors"
            >
              EXPLORE THE MARKETPLACE
            </Link>
          </div>
        ) : (
          <>
            <div className="mt-10 space-y-4">
              <AnimatePresence>
                {items.map(({ product, quantity }) => {
                  if (!product) return null;
                  const category = getCategory(product.category);
                  return (
                    <motion.div
                      key={product.id}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.25 }}
                      className="flex items-center gap-4 p-3 rounded-xl border border-white/10 bg-white/5"
                    >
                      <div
                        className="w-20 h-20 rounded-lg shrink-0 overflow-hidden bg-center bg-cover"
                        style={
                          product.images[0]
                            ? { backgroundImage: `url(${product.images[0]})` }
                            : { background: category?.gradient }
                        }
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{product.title}</p>
                        <p className="text-[var(--nm-ink-muted)] text-xs mt-1">
                          Qty {quantity}
                        </p>
                      </div>
                      <p className="text-white font-display font-semibold">
                        {formatPrice(product.price * quantity)}
                      </p>
                      <button
                        onClick={() => removeFromCart(product.id)}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--nm-ink-muted)] hover:text-white hover:bg-white/10 transition-colors"
                        aria-label="Remove"
                      >
                        <X size={16} />
                      </button>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>

            <div className="mt-10 flex items-center justify-between border-t border-white/10 pt-6">
              <span className="text-[var(--nm-ink-muted)]">Total</span>
              <span className="font-display text-2xl font-semibold text-white">
                {formatPrice(total)}
              </span>
            </div>
            {!authReady ? null : user ? (
              <>
                {error && (
                  <p className="mt-4 text-sm text-[var(--nm-pink)]">{error}</p>
                )}
                <button
                  onClick={handleCheckout}
                  disabled={submitting}
                  className="mt-6 w-full py-4 rounded-full bg-white text-[#05070d] text-sm font-medium tracking-wide hover:bg-[var(--nm-blue-bright)] hover:text-white transition-colors disabled:opacity-50"
                >
                  {submitting ? "REDIRECTING TO STRIPE…" : "CHECKOUT WITH STRIPE"}
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="mt-6 block text-center w-full py-4 rounded-full bg-white text-[#05070d] text-sm font-medium tracking-wide hover:bg-[var(--nm-blue-bright)] hover:text-white transition-colors"
              >
                SIGN IN TO CHECK OUT
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
}
